import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { cn } from '../../src/lib/utils';
import { LANGUAGES } from '../../constants';
import { useStore } from '../../src/lib/store';

interface LanguageSelectorProps {
  className?: string;
  onLanguageChange?: (code: string) => void;
  variant?: 'light' | 'dark';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className, 
  onLanguageChange,
  variant = 'light'
}) => {
  const { t, i18n } = useTranslation();
  const { setLanguage: setGlobalLanguage } = useStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = async (code: string) => {
    setGlobalLanguage(code);
    if (onLanguageChange) {
      onLanguageChange(code);
    }
    setIsOpen(false);
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "px-3 py-2 rounded-full transition-all active:scale-90 flex items-center justify-center gap-2",
          variant === 'dark' 
            ? "bg-white/5 text-white hover:bg-white/10 border border-white/10" 
            : "bg-gray-50 text-[#1a2b3c] hover:bg-gray-100 border border-gray-100"
        )}
        title={t('settings.language')}
      >
        <Globe size={18} className="opacity-60" />
        <span className="text-lg leading-none">{currentLanguage.flag}</span>
      </button>

      {isOpen && (
        <div className={cn(
          "absolute right-0 mt-2 w-48 rounded-2xl shadow-2xl border z-[1001] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200",
          variant === 'dark'
            ? "bg-[#0D1526]/95 backdrop-blur-xl border-white/10"
            : "bg-white border-gray-100"
        )}>
          <div className="py-2">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={cn(
                  "w-full px-4 py-3 flex items-center justify-between transition-colors",
                  variant === 'dark'
                    ? "hover:bg-white/5 text-white/80 hover:text-white"
                    : "hover:bg-gray-50 text-gray-700 hover:text-[#1a2b3c]",
                  i18n.language === lang.code && (variant === 'dark' ? "bg-white/5 text-white" : "bg-emerald-50 text-emerald-600")
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{lang.flag}</span>
                  <span className="text-sm font-bold">{lang.name}</span>
                </div>
                {i18n.language === lang.code && <Check size={16} className="text-emerald-500" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
